import { formOptions } from '@tanstack/react-form';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Activity, CircleHelp } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Spinner from '@/components/Common/Spinner';
import { Stage } from '@/components/Stage/Stage';
import {
  previewSignalMutationOptions,
  type PreviewSignalResponse,
  signalListQueryOptions,
} from '@/entities/signal';
import { captureException } from '@/shared/lib/sentry';
import { withForm } from '@/shared/ui';
import { Button } from '@/shared/ui/button';
import { Tooltip } from '@/shared/ui/tooltip';
import { toast } from '@/stores/pure/ToastStore';

import { useComputedIssueList } from '../../signals';
import {
  createSignalFormOptions,
  createTitleSchema,
  definitionSchema,
} from '../model/schema';
import { SignalPreviewTable } from './signal-preview-table';

type PreviewState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'loaded'; data: PreviewSignalResponse };

const createSignalFormOptionsWithProps = formOptions({
  ...createSignalFormOptions,
  props: {
    signalId: '',
    workspaceId: '',
    isSubmitting: false,
    originalTitle: '',
    onPreviewComplete: undefined as (() => void) | undefined,
    onFormValueChange: undefined as (() => void) | undefined,
  },
});

// form value 변경 감지 컴포넌트
const FormValueWatcher = withForm({
  ...createSignalFormOptionsWithProps,
  render: ({ form, onFormValueChange }) => {
    function Watcher() {
      const prevRef = useRef<string | null>(null);

      return (
        <form.Subscribe
          selector={(state: any) =>
            `${state.values.title}|${state.values.definition}`
          }
        >
          {(combined: string) => {
            if (prevRef.current !== null && prevRef.current !== combined) {
              onFormValueChange?.();
            }
            prevRef.current = combined;
            return null;
          }}
        </form.Subscribe>
      );
    }

    return <Watcher />;
  },
});

export const SignalFormContent = withForm({
  ...createSignalFormOptionsWithProps,
  render: ({
    form,
    workspaceId,
    isSubmitting,
    signalId,
    originalTitle,
    onPreviewComplete,
    onFormValueChange,
  }) => {
    function Content() {
    const { t } = useTranslation('signal');
    const { mutateAsync: fetchPreviewData } = useMutation(
      previewSignalMutationOptions(),
    );
    const { computedIssues } = useComputedIssueList();
    const { data: signalList } = useQuery(
      signalListQueryOptions({ workspaceId }),
    );

    // update 모드에서는 현재 시그널 제목 제외
    const existingTitles = useMemo(() => {
      if (!signalList) return [];
      return signalList
        .filter((s) => s.id !== signalId)
        .map((s) => s.title);
    }, [signalList, signalId]);

    // Zod 스키마 기반 필드 유효성 검사
    const titleSchema = useMemo(
      () => createTitleSchema(existingTitles),
      [existingTitles],
    );

    /**
     * FIXME: IS_CREATE_SIGNAL_MODE 사용하지 말고 update, create 컴포넌트를 분리하기
     * signalId가 없으면 Create mode
     * signalId가 있으면 Update mode
     */
    const IS_CREATE_SIGNAL_MODE = !signalId;

    const [preview, setPreview] = useState<PreviewState>(() => ({
      status: signalId ? 'loaded' : 'idle',
      data: signalId
        ? computedIssues.map((v) => ({
            name: v.name,
            change: v.signalIds.includes(signalId) ? 'SUSPENDED' : 'NONE',
          }))
        : [],
    }));

    const handlePreview = async () => {
      // 클릭 시 모든 필드 유효성 검사 트리거 (에러 메시지 inline 표시)
      await form.validateAllFields('change');
      if (!form.state.isFieldsValid) return;

      setPreview({ status: 'loading' });

      try {
        const data = await fetchPreviewData({
          id: IS_CREATE_SIGNAL_MODE ? undefined : signalId,
          workspace_id: workspaceId,
          title: form.getFieldValue('title'),
          definition: form.getFieldValue('definition'),
        });
        setPreview({ status: 'loaded', data });
        onPreviewComplete?.();
      } catch (error: any) {
        toast('Failed to preview signal', 'fail');
        setPreview({ status: 'idle' });
        captureException(error);
      }
    };

      return (
      <div className="flex h-full gap-6">
        {/* 좌측: 입력 폼 */}
        <div className="flex w-[440px] shrink-0 flex-col gap-5">
          <form.AppField
            name="title"
            validators={{
              onChange: ({ value }) => {
                const result = titleSchema.safeParse(value);
                if (!result.success) return result.error.issues[0].message;
                return undefined;
              },
            }}
            children={(field) => (
              <field.LabelGroup label="Title">
                <field.TextField placeholder="Enter title" />
              </field.LabelGroup>
            )}
          />

          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-1">
              <label className="text-sm font-medium leading-none">
                {t('What_type_of_issues_is_this_signal_intended_to')}
              </label>
              <Tooltip
                content={
                  <p className="max-w-[240px]">
                    {t('The_text_you_enter_will_be_used_as_classification')}
                  </p>
                }
                side="top"
              >
                <CircleHelp className="text-muted-foreground size-4" />
              </Tooltip>
            </div>
            <form.AppField
              name="definition"
              validators={{
                onChange: ({ value }) => {
                  const result = definitionSchema.safeParse(value);
                  if (!result.success) return result.error.issues[0].message;
                  return undefined;
                },
              }}
              children={(field) => (
                <field.Textarea
                  className="resize-none h-[100px]"
                  placeholder={t(
                    'e_g__Safety_related_issues_such_as_skin_irritation',
                  )}
                  rows={4}
                />
              )}
            />
          </div>

          <form.Subscribe
            selector={(state) => state.isDefaultValue}
          >
            {(isDefaultValue) => (
              <Button
                variant="default"
                className="w-full"
                onClick={handlePreview}
                isLoading={preview.status === 'loading'}
                disabled={
                  isSubmitting ||
                  (!IS_CREATE_SIGNAL_MODE && isDefaultValue)
                }
              >
                Preview matching issues
              </Button>
            )}
          </form.Subscribe>
        </div>

        {/* 우측: 프리뷰 영역 */}
        <div className="flex h-full flex-1 flex-col overflow-hidden rounded-lg bg-[#F6F6F4] p-5">
          <Stage className="h-full">
            <Stage.Scene
              status={preview.status === 'idle'}
              className="h-full"
              default
            >
              <div className="flex h-full flex-col items-center justify-center gap-3">
                <Activity className="text-muted-foreground size-8" />
                <p className="text-sm font-semibold">
                  See which issues match your signal
                </p>
                <p className="text-muted-foreground text-sm">
                  Enter a description and preview to find issues that fit this
                  signal.
                </p>
              </div>
            </Stage.Scene>

            <Stage.Scene
              status={preview.status === 'loading'}
              className="h-full"
            >
              <div className="flex h-full items-center justify-center">
                <Spinner size={24} />
              </div>
            </Stage.Scene>

            <Stage.Scene
              status={preview.status === 'loaded'}
              className="h-full"
              data={preview.status === 'loaded' ? preview.data : undefined}
            >
              {(data: PreviewSignalResponse) => (
                <form.Subscribe selector={(state) => state.values.title}>
                  {(title) => (
                    <SignalPreviewTable
                      data={data}
                      signalTitle={title}
                      originalTitle={originalTitle}
                      isUpdateMode={!IS_CREATE_SIGNAL_MODE}
                    />
                  )}
                </form.Subscribe>
              )}
            </Stage.Scene>
          </Stage>
        </div>

        {onFormValueChange && (
          <FormValueWatcher
            form={form}
            signalId={signalId}
            workspaceId={workspaceId}
            isSubmitting={isSubmitting}
            originalTitle={originalTitle}
            onPreviewComplete={onPreviewComplete}
            onFormValueChange={onFormValueChange}
          />
        )}
      </div>
      );
    }

    return <Content />;
  },
});
